import {
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	PrinterOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import { Button, Col, Drawer, Row, Space, Table, Tag } from "antd";
import React, { useEffect, useState } from "react";
import {
	deleteCierreDeCaja,
	getAllCierresDeCaja,
	getCompleteCierreDeCaja,
} from "../../services/cierreDeCajaService";
import { syncCierreDeCajaToBC } from "../../services/cierreDeCajaSyncService";
import CierreTicket from "./CierreTicket";
import "./CierreDeCajaStyles.css";

const CierreDeCajaList = ({ refreshTrigger, onEdit }) => {
	const [cierres, setCierres] = useState([]);
	const [loading, setLoading] = useState(false);
	const [syncLoading, setSyncLoading] = useState(false);
	const [selectedCierre, setSelectedCierre] = useState(null);
	const [detailVisible, setDetailVisible] = useState(false);
	const [ticketVisible, setTicketVisible] = useState(false);
	const [cierreForTicket, setCierreForTicket] = useState(null);

	useEffect(() => {
		loadCierres();
	}, [refreshTrigger]);

	const loadCierres = async () => {
		try {
			setLoading(true);
			const cierresList = await getAllCierresDeCaja();
			setCierres(cierresList);
		} catch (error) {
			console.error("Error al cargar cierres de caja:", error);
			window.messageApi.error("No se pudieron cargar los cierres de caja");
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = (cierre) => {
		if (cierre.No) {
			window.messageApi.warning(
				"No se puede editar un cierre de caja que ya ha sido sincronizado con Business Central"
			);
			return;
		}
		onEdit(cierre);
	};

	const handleDelete = async (cierre) => {
		if (cierre.No) {
			window.messageApi.warning(
				"No se puede eliminar un cierre de caja que ya ha sido sincronizado con Business Central"
			);
			return;
		}

		if (
			window.confirm(
				"¿Está seguro de eliminar este cierre de caja? Esta acción no se puede deshacer"
			)
		) {
			try {
				setLoading(true);
				await deleteCierreDeCaja(cierre.id);
				window.messageApi.success("Cierre de caja eliminado correctamente");
				await loadCierres();
			} catch (error) {
				console.error("Error al eliminar cierre de caja:", error);
				window.messageApi.error(
					error.message || "No se pudo eliminar el cierre de caja"
				);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleSync = async (cierre) => {
		try {
			setSyncLoading(true);
			const result = await syncCierreDeCajaToBC(cierre);

			if (result.success) {
				window.messageApi.success("Cierre de caja sincronizado correctamente");
				loadCierres();
			} else {
				window.messageApi.error(`Error al sincronizar: ${result.message}`);
			}
		} catch (error) {
			console.error("Error al sincronizar cierre de caja:", error);
			window.messageApi.error("No se pudo sincronizar el cierre de caja");
		} finally {
			setSyncLoading(false);
		}
	};

	const handleViewDetail = async (cierre) => {
		try {
			setLoading(true);
			const completeCierre = await getCompleteCierreDeCaja(cierre.id);
			setSelectedCierre(completeCierre);
			setDetailVisible(true);
		} catch (error) {
			console.error("Error al obtener detalle de cierre de caja:", error);
			window.messageApi.error(
				"No se pudo obtener el detalle del cierre de caja"
			);
		} finally {
			setLoading(false);
		}
	};

	const handleViewTicket = async (cierre) => {
		try {
			setLoading(true);
			const completeCierre = await getCompleteCierreDeCaja(cierre.id);
			setCierreForTicket(completeCierre);
			setTicketVisible(true);
		} catch (error) {
			console.error("Error al obtener detalle de cierre de caja:", error);
			window.messageApi.error(
				"No se pudo obtener el detalle del cierre de caja"
			);
		} finally {
			setLoading(false);
		}
	};

	const getSyncStatusTag = (cierre) => {
		if (cierre.deleted) {
			return <Tag color="red">Eliminado</Tag>;
		}
		if (cierre.pendingSync) {
			return <Tag color="orange">Pendiente</Tag>;
		}
		if (cierre.lastSynced) {
			return <Tag color="green">Sincronizado</Tag>;
		}
		return <Tag color="default">Sin sincronizar</Tag>;
	};

	const getEstadoTag = (estado) => {
		if (estado === "Cerrado") {
			return <Tag color="red">Cerrado</Tag>;
		}
		if (estado === "Abierto") {
			return <Tag color="green">Abierto</Tag>;
		}
		return <Tag color="default">{estado}</Tag>;
	};

	const columns = [
		{
			title: "Nº",
			dataIndex: "No",
			key: "No",
			render: (no) => no || "-",
		},
		{
			title: "Cajero",
			dataIndex: "Cajero",
			key: "Cajero",
		},
		{
			title: "Fecha Apertura",
			dataIndex: "FechaDeApertura",
			key: "FechaDeApertura",
			render: (date) => {
				if (!date) return "-";
				return new Date(date).toISOString().split("T")[0];
			},
		},
		{
			title: "Fecha Cierre",
			dataIndex: "FechaDeCierre",
			key: "FechaDeCierre",
			render: (date) => {
				if (!date) return "-";
				return new Date(date).toISOString().split("T")[0];
			},
		},
		{
			title: "Estado",
			dataIndex: "Estado",
			key: "Estado",
			render: (estado) => getEstadoTag(estado),
		},
		{
			title: "Sincronización",
			key: "syncStatus",
			render: (_, record) => getSyncStatusTag(record),
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="small">
					<Button
						icon={<EyeOutlined />}
						size="small"
						onClick={() => handleViewDetail(record)}
						title="Ver detalle"
					/>
					<Button
						icon={<PrinterOutlined />}
						size="small"
						onClick={() => handleViewTicket(record)}
						title="Ver/Imprimir comprobante"
					/>
					<Button
						icon={<EditOutlined />}
						size="small"
						onClick={() => handleEdit(record)}
						disabled={!!record.No}
						title={
							record.No
								? "No se puede editar un cierre sincronizado"
								: "Editar cierre de caja"
						}
					/>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						danger
						onClick={() => handleDelete(record)}
						disabled={!!record.No || record.deleted}
						title={
							record.No
								? "No se puede eliminar un cierre sincronizado"
								: record.deleted
									? "Este cierre ya ha sido eliminado"
									: "Eliminar cierre de caja"
						}
					/>
					<Button
						icon={<SyncOutlined />}
						size="small"
						type="primary"
						onClick={() => handleSync(record)}
						loading={syncLoading}
						disabled={!!record.No || record.deleted}
						title={
							record.No
								? "El cierre ya está sincronizado"
								: "Sincronizar con Business Central"
						}
					/>
				</Space>
			),
		},
	];

	return (
		<div className="cierreDeCaja-list-container">
			<h2>Listado de cierres de caja</h2>

			<Table
				columns={columns}
				dataSource={cierres}
				rowKey="id"
				loading={loading}
				pagination={{ pageSize: 10 }}
				scroll={{ x: "max-content" }}
			/>

			{selectedCierre && (
				<Drawer
					title={`Detalle de cierre de caja: ${selectedCierre.No || "Sin número"
						}`}
					placement="right"
					onClose={() => setDetailVisible(false)}
					open={detailVisible}
					width={700}
					className="cierreDeCaja-detail-drawer"
				>
					<div>
						<h3>Información general</h3>
						<p>
							<strong>Nº:</strong> {selectedCierre.No || "-"}
						</p>
						<p>
							<strong>Cajero:</strong> {selectedCierre.Cajero}
						</p>
						<p>
							<strong>Importe de Apertura:</strong>{" "}
							{selectedCierre.ImporteDeApertura?.toFixed(2) || "0.00"} $
						</p>
						<p>
							<strong>Estado:</strong> {getEstadoTag(selectedCierre.Estado)}
						</p>

						<Row>
							<Col span={12}>
								<p>
									<strong>Fecha de Apertura:</strong>{" "}
									{selectedCierre.FechaDeApertura
										? new Date(
											selectedCierre.FechaDeApertura
										).toISOString().split('T')[0]
										: "-"}
								</p>
							</Col>
							<Col span={12}>
								<p>
									<strong>Fecha de Cierre:</strong>{" "}
									{selectedCierre.FechaDeCierre
										? new Date(
											selectedCierre.FechaDeCierre
										).toISOString().split('T')[0]
										: "-"}
								</p>
							</Col>
						</Row>

						<div className="multi-currency-section">
							<h3>Montos de cierre por moneda</h3>
							<Row>
								<Col span={8}>
									<p>
										<strong>BS:</strong>{" "}
										{selectedCierre.ImporteDeCierreBS?.toFixed(2) || "0.00"}
									</p>
								</Col>
								<Col span={8}>
									<p>
										<strong>USD:</strong>{" "}
										{selectedCierre.ImporteDeCierreUS?.toFixed(2) || "0.00"}
									</p>
								</Col>
								<Col span={8}>
									<p>
										<strong>EUR:</strong>{" "}
										{selectedCierre.ImporteDeCierreEUR?.toFixed(2) || "0.00"}
									</p>
								</Col>
							</Row>
						</div>

						<div className="arqueo-section">
							<h3>Arqueo de caja</h3>
							<Row>
								<Col span={6}>
									<p>
										<strong>Efectivo USD:</strong>{" "}
										{selectedCierre.ArqueoEfectivoUS?.toFixed(2) || "0.00"}
									</p>
								</Col>
								<Col span={6}>
									<p>
										<strong>Tarjeta USD:</strong>{" "}
										{selectedCierre.ArqueoTarjetaUS?.toFixed(2) || "0.00"}
									</p>
								</Col>
								<Col span={6}>
									<p>
										<strong>Total USD:</strong>{" "}
										{((selectedCierre.ArqueoEfectivoUS || 0) + (selectedCierre.ArqueoTarjetaUS || 0)).toFixed(2)}
									</p>
								</Col>
								<Col span={6}>
									<p>
										<strong>Legacy USD:</strong>{" "}
										{selectedCierre.ArqueoUS?.toFixed(2) || "0.00"}
									</p>
								</Col>
							</Row>
							<Row style={{ marginTop: "16px" }}>
								<Col span={8}>
									<p>
										<strong>BS:</strong>{" "}
										{selectedCierre.ArqueoBS?.toFixed(2) || "0.00"}
									</p>
								</Col>
								<Col span={8}>
									<p>
										<strong>EUR:</strong>{" "}
										{selectedCierre.ArqueoEUR?.toFixed(2) || "0.00"}
									</p>
								</Col>
							</Row>
						</div>

						<h3 style={{ marginTop: "24px" }}>Detalles</h3>
						<Table
							columns={[
								{
									title: "Factura",
									dataIndex: "invoiceNo",
									key: "invoiceNo",
									render: (invoiceNo, record) => {
										// Si tiene No (número de BC), mostrar el No, sino mostrar el ID local
										if (invoiceNo && invoiceNo !== "Desconocido") {
											return invoiceNo;
										} else if (record.invoiceId) {
											return `ID: ${record.invoiceId}`;
										}
										return "-";
									},
								},
								{
									title: "Forma de Pago",
									dataIndex: "idFormaPago",
									key: "idFormaPago",
								},
								{
									title: "Descripción",
									dataIndex: "DesFormaPago",
									key: "DesFormaPago",
								},
								{
									title: "Monto",
									dataIndex: "MontoPago",
									key: "MontoPago",
									render: (monto) => `${monto?.toFixed(2) || "0.00"} $`,
								},
							]}
							dataSource={selectedCierre.detalles || []}
							pagination={false}
							scroll={{ x: "max-content" }}
						/>

						{selectedCierre.detalles && selectedCierre.detalles.length > 0 && (
							<div
								className="cierreDeCaja-totals"
								style={{ marginTop: "24px" }}
							>
								<div className="cierreDeCaja-totals-item grand-total">
									<span>Total:</span>
									<span>
										{selectedCierre.detalles
											.reduce(
												(sum, detalle) => sum + (detalle.MontoPago || 0),
												0
											)
											.toFixed(2)}{" "}
										$
									</span>
								</div>
							</div>
						)}
					</div>
				</Drawer>
			)}

			{/* Modal del ticket de cierre */}
			{cierreForTicket && (
				<CierreTicket
					open={ticketVisible}
					onClose={() => {
						setTicketVisible(false);
						setCierreForTicket(null);
					}}
					cierre={cierreForTicket}
					detalles={cierreForTicket.detalles || []}
					caja={cierreForTicket.Caja}
					tpv={cierreForTicket.TPV}
				/>
			)}
		</div>
	);
};

export default CierreDeCajaList;
